<template>
  <div class="chart-wrapper">
    <div class="chart-container">
      <highcharts :options="chartOptions" ref="chart"></highcharts>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'

export default {
  name: 'PerformanceHighchart',
  components: {
    highcharts: Chart
  },
  props: {
    period: {
      type: String,
      default: 'daily'
    },
    data: {
      type: Array,
      default: () => []
    },
    user: {
      type: Object,
      default: () => ({})
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    showLegend: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chartOptions: {
        chart: {
          type: 'spline',
          height: 300,
          width: null,
          backgroundColor: 'transparent',
          style: {
            fontFamily: 'inherit',
            fontWeight: '500'
          },
          spacing: [20, 20, 20, 40],
          renderTo: 'chart-container'
        },
        title: {
          text: null
        },
        credits: {
          enabled: false
        },
        accessibility: {
          enabled: false
        },
        legend: {
          align: 'center',
          verticalAlign: 'top',
          layout: this.showLegend ? 'vertical' : 'horizontal',
          itemStyle: {
            color: '#000000',
            fontWeight: '500',
            fontSize: '12px'
          },
          itemHoverStyle: {
            color: '#7367f0'
          },
          symbolWidth: 8,
          symbolHeight: 8,
          itemDistance: 15,
          padding: 5,
          margin: 5,
          enabled: true
        },
        xAxis: {
          categories: [],
          labels: {
            style: {
              fontSize: '12px',
              color: '#6e6b7b'
            },
            rotation: 0,
            align: 'center',
            y: 25
          },
          tickLength: 0,
          lineWidth: 1,
          lineColor: '#ebe9f1'
        },
        yAxis: [{
          title: {
            text: 'Revenue (RM)',
            style: {
              fontSize: '12px',
              color: '#6e6b7b'
            },
            align: 'middle',
            rotation: 270,
            y: 0
          },
          labels: {
            style: {
              fontSize: '12px',
              color: '#6e6b7b'
            },
            x: -10
          },
          gridLineColor: '#ebe9f1',
          gridLineWidth: 1
        }, {
          title: {
            text: 'Patients',
            style: {
              fontSize: '12px',
              color: '#6e6b7b'
            },
            align: 'middle',
            rotation: 270,
            y: 0
          },
          opposite: true,
          labels: {
            style: {
              fontSize: '12px',
              color: '#6e6b7b'
            },
            x: 10
          },
          gridLineColor: '#ebe9f1',
          gridLineWidth: 1
        }],
        tooltip: {
          shared: true,
          backgroundColor: 'white',
          borderColor: '#ebe9f1',
          borderRadius: 4,
          borderWidth: 1,
          shadow: true,
          style: {
            fontSize: '12px'
          },
          headerFormat: '<span style="font-size: 12px; font-weight: 500; color: #5e5873">{point.key}</span><br/>',
          pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b style="font-weight: 600">{point.y:,.0f}</b><br/>',
          valueDecimals: 0
        },
        plotOptions: {
          series: {
            marker: {
              radius: 4,
              symbol: 'circle'
            },
            lineWidth: 2
          }
        },
        responsive: {
          rules: [{
            condition: {
              maxWidth: 480
            },
            chartOptions: {
              chart: {
                height: 200,
                spacing: [8, 0, 8, 5]
              },
              legend: {
                enabled: this.showLegend ? true : false
              },
              xAxis: {
                labels: {
                  style: {
                    fontSize: '9px'
                  },
                  rotation: 0,
                  step: 3
                }
              },
              yAxis: [{
                title: {
                  style: {
                    fontSize: '9px'
                  }
                },
                labels: {
                  style: {
                    fontSize: '9px'
                  }
                }
              }, {
                title: {
                  style: {
                    fontSize: '9px'
                  }
                },
                labels: {
                  style: {
                    fontSize: '9px'
                  }
                }
              }],
              tooltip: {
                style: {
                  fontSize: '10px'
                },
                headerFormat: '<span style="font-size: 10px; font-weight: 500; color: #5e5873">{point.key}</span><br/>',
                pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b style="font-weight: 600">{point.y:,.0f}</b><br/>'
              },
              plotOptions: {
                series: {
                  marker: {
                    radius: 2
                  }
                }
              }
            }
          }, {
            condition: {
              minWidth: 481,
              maxWidth: 1024
            },
            chartOptions: {
              chart: {
                height: 250,
                spacing: [15, 10, 15, 35]
              }
            }
          }]
        }
      }
    }
  },
  computed: {
    volumeAxisLabel () {
      const n = this.user?.subsidiary_name || ''
      return /diagnostic/i.test(n) ? 'Lab tests' : 'Patients'
    },
    formattedData() {
      
      if (!this.data || !Array.isArray(this.data)) {
        return {
          labels: [],
          revenueData: [],
          patientData: []
        };
      }

      // For daily data
      if (this.period === 'daily') {
        // Sort data by date
        const sortedData = [...this.data].sort((a, b) => new Date(a.date) - new Date(b.date));
        
        const labels = sortedData.map(item => {
          const date = new Date(item.date);
          return date.getDate().toString();
        });
        const revenueData = sortedData.map(item => parseFloat(item.revenue) || 0);
        const patientData = sortedData.map(item => parseInt(item.patients) || 0);
        
        return { labels, revenueData, patientData };
      }
      
      // For weekly data
      if (this.period === 'weekly') {
        
        // Convert data object to array if needed
        const dataArray = Array.isArray(this.data) ? this.data : Object.values(this.data)
        
        // Sort data by week start date
        const sortedData = dataArray.sort((a, b) => {
          const aStart = new Date(a.date)
          const bStart = new Date(b.date)
          return aStart - bStart
        })
        
        // Format labels to show week number and dates
        const labels = sortedData.map(item => {
          const date = new Date(item.date)
          const weekNumber = Math.ceil(date.getDate() / 7)
          const startDate = date.getDate()
          
          // Calculate end date
          let endDate
          if (weekNumber === 5) {
            // For the last week, use the last day of the month
            endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
          } else {
            // For other weeks, add 6 days to start date
            endDate = new Date(date)
            endDate.setDate(date.getDate() + 6)
            endDate = endDate.getDate()
          }
          
          return `Week ${weekNumber} (${startDate}-${endDate})`
        })
        
        const revenueData = sortedData.map(item => parseFloat(item.revenue) || 0)
        const patientData = sortedData.map(item => parseInt(item.patients) || 0)
        
        return { labels, revenueData, patientData }
      }
      
      // For monthly data
      if (this.period === 'monthly') {
        
        // Sort data by month
        const sortedData = [...this.data].sort((a, b) => {
          // Get month index from month name
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return months.indexOf(a.date) - months.indexOf(b.date);
        });

        const labels = sortedData.map(item => item.date); // Use the already formatted month name
        const revenueData = sortedData.map(item => parseFloat(item.revenue) || 0);
        const patientData = sortedData.map(item => parseInt(item.patients) || 0);
        
        return { labels, revenueData, patientData };
      }

      return {
        labels: [],
        revenueData: [],
        patientData: []
      };
    }
  },
  watch: {
    user: {
      handler () {
        this.updateChart(this.formattedData)
      },
      deep: true
    },
    isMobile: {
      handler(newValue) {
        this.updateChart(this.formattedData)
      },
      immediate: true
    },
    data: {
      handler(newValue) {
        this.updateChart(this.formattedData)
      },
      immediate: true
    },
    showLegend: {
      handler(newValue) {
        this.updateChart(this.formattedData)
      },
      immediate: true
    }
  },
  methods: {
    // Helper function to get week number
    getWeekNumber(date) {
      const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
      const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
      return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    },
    updateChart(data) {
      
      // Get the month from the first data point
      let monthName = 'Current Month'
      if (data.labels && data.labels.length > 0) {
        const firstDate = new Date(this.data[0].date)
        monthName = firstDate.toLocaleString('default', { month: 'long' })
      }
      
      this.chartOptions = {
        ...this.chartOptions,
        xAxis: {
          ...this.chartOptions.xAxis,
          categories: data.labels,
          title: {
            text: this.period === 'weekly' ? `${monthName} Weeks` : 
                  this.period === 'monthly' ? 'Monthly Overview' : 
                  monthName,
            style: {
              color: '#6e6b7b',
              fontSize: '12px',
              fontWeight: '500'
            }
          },
          min: 0,
          max: data.labels.length - 1,
          tickInterval: 1
        },
        yAxis: [{
          ...this.chartOptions.yAxis[0],
          min: 0,
          startOnTick: true,
          showFirstLabel: true
        }, {
          ...this.chartOptions.yAxis[1],
          title: {
            ...this.chartOptions.yAxis[1].title,
            text: this.volumeAxisLabel
          },
          min: 0,
          startOnTick: true,
          showFirstLabel: true
        }],
        plotOptions: {
          ...this.chartOptions.plotOptions,
          series: {
            ...this.chartOptions.plotOptions.series,
            connectNulls: true,
            marker: {
              enabled: true,
              radius: 3,
              symbol: 'circle'
            }
          }
        },
        series: [
          {
            name: 'Revenue',
            data: data.revenueData,
            color: '#3B82F6',
            yAxis: 0,
            type: 'spline'
          },
          {
            name: this.volumeAxisLabel,
            data: data.patientData,
            color: '#10B981',
            yAxis: 1,
            type: 'spline'
          }
        ],
        legend: {
          ...this.chartOptions.legend,
          enabled: this.showLegend ? true : this.chartOptions.legend.enabled
        }
      }
    }
  },
  mounted() {

  }
}
</script>

<style scoped>
.chart-wrapper {
  background: white;
  border-radius: 8px;
  padding: 20px;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.chart-container {
  width: 100%;
  height: 300px;
  background: transparent;
  position: relative;
}

.chart-container :deep(.highcharts-container) {
  width: 100% !important;
  height: 100% !important;
}

.chart-container :deep(.highcharts-background) {
  width: 100% !important;
  height: 100% !important;
}

@media (max-width: 1024px) {
  .chart-wrapper {
    padding: 15px;
  }
  
  .chart-container {
    height: 250px;
  }
}

@media (max-width: 480px) {
  .chart-wrapper {
    padding: 0;
  }
  
  .chart-container {
    height: 200px;
  }
}
</style> 